import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import { Auth } from 'aws-amplify';
import * as yup from 'yup';
import { TextField, Button, Box } from '@material-ui/core';
import { useSnackbar } from 'notistack';

/**
 * i18n
 */
import { useIntl } from 'react-intl';
import { FormattedMessage } from 'react-intl';

import layoutStyles from 'styles/layout.module.scss';
import styles from 'styles/login.module.scss';

function VerifyUser({
  setAuthState,
  setUser,
  setUsername,
  setPassword,
  username,
  password
}) {
  const [submitting, setSubmitting] = useState(false);
  const [resetStep, setResetStep] = useState(username !== '' ? 'code' : 'username');
  const [resetError, setResetError] = useState('');
  const [codeResendStatus, setCodeResendStatus] = useState('');

  const intl = useIntl();
  const urlPrefix = intl.locale === 'en' ? '' : `/${intl.locale}`;
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const resendCode = () => {
    setCodeResendStatus('SENDING');
    Auth.resendSignUp(username);
    setCodeResendStatus('SENT');
  };

  const validationSchemaUsername = yup.object({
    username: yup
      .string()
      .matches(/^\S+@\S+\.\w\w+$/, intl.formatMessage({id:'forms.signIn.matchEmail', description:'string'}))
      .required(intl.formatMessage({id:'forms.signIn.usernameRequired', description:'string'}))
  });

  const formikUsername = useFormik({
    initialValues: {
      username: ''
    },
    validationSchema: validationSchemaUsername,
    onSubmit: async ({ username }) => {
      setResetError('')
      setSubmitting( true );
      try {
        await Auth.resendSignUp(username);
        setUsername(username);
        setResetStep('code');
      } catch (err) {
        setResetError(err.message);
      }
      setSubmitting(false);
    },
  });

  const validationSchemaCode = yup.object({
    code: yup
      .string()
      .min(6)
      .max(6)
      .required()
  });

  const formikCode = useFormik({
    initialValues: {
      code: ''
    },
    validationSchema: validationSchemaCode,
    onSubmit: async ({ code }) => {
      setResetError('')
      setSubmitting(true);
      try {
        await Auth.confirmSignUp(username, code);
        enqueueSnackbar(
          intl.formatMessage({id:'forms.signUp.verified', description:'string'}),
          {"variant": "success"}
        );
        if (password !== '') {
          const user = await Auth.signIn(username, password);
          setUser(user);
          setPassword('')
          setAuthState('signedin');
        }
        history.push(`${urlPrefix}/login`);
      } catch (err) {
        if (err.message === 'User cannot be confirmed. Current status is CONFIRMED') {
          enqueueSnackbar(
            intl.formatMessage({id:'forms.signUp.verified', description:'string'}),
            {"variant": "success"}
          );
          if (password !== '') {
            const user = await Auth.signIn(username, password);
            setUser(user);
            setPassword('')
            setAuthState('signedin');
          }
          history.push(`${urlPrefix}/login`);
        } else {
          setCodeResendStatus('');
          setResetError(err.message);
        }
      }
      setSubmitting(false);
    },
  });

  const render = () => {
    if (resetStep === 'username') {
      return (
        <form onSubmit={formikUsername.handleSubmit} className={styles.loginFlow__form}>
          <div className={styles.passwordRequirements}>
            <FormattedMessage id="forms.signUp.sendingCode" />
          </div>
          <Box mt={2}>
            <TextField
              FormHelperTextProps={{
                className: styles.helperText
              }}
              error={formikUsername.touched.username && Boolean(formikUsername.errors.username)}
              fullWidth
              helperText={formikUsername.touched.username && formikUsername.errors.username}
              label={intl.formatMessage({id:'forms.signIn.username', description:'string'})}
              placeholder={intl.formatMessage({id:'forms.signIn.enterUsername', description:'string'})}
              name="username"
              onBlur={formikUsername.handleBlur}
              onChange={formikUsername.handleChange}
              type="email"
              value={formikUsername.values.username}
              variant="filled"
            />
          </Box>
          { resetError.length > 0 && (
              <div className={styles.errorMessage__container}>
                <label className={styles.errorMessage}>{resetError}</label>
              </div>
            )
          }
          <div className={styles.infoMessageSubmit__container}>
            <label className={styles.infoMessage}>
              <Link to={'/login'}>
                <FormattedMessage id="forms.resetPassword.backToSignIn" />
              </Link>
            </label>
            <Button
              color="secondary"
              disabled={submitting}
              size="large"
              type="submit"
              variant="contained"
            >
              { submitting ? (
                  <div className="spinner-border text-primary" role="status">
                    <span className="sr-only">
                      {intl.formatMessage({id:'forms.signIn.loading', description:'string'})}
                    </span>
                  </div>
                ) : (
                  intl.formatMessage({id:'forms.resetPassword.sendCode', description:'string'})
                )
              }
            </Button>
          </div>
        </form>
      );
    } else if (resetStep === 'code') {
      return (
        <form onSubmit={formikCode.handleSubmit} className={styles.loginFlow__form}>
          <div className={styles.passwordRequirements}>
            <FormattedMessage id="forms.signUp.sentCode" />
          </div>
          <Box mb={1} />
          <div className={styles.passwordRequirements}>
            <FormattedMessage id="forms.signUp.checkSpam" />
          </div>
          <Box mb={3}>
            <TextField
              FormHelperTextProps={{
                className: styles.helperText
              }}
              error={formikCode.touched.code && Boolean(formikCode.errors.code)}
              fullWidth
              helperText={formikCode.touched.code && formikCode.errors.code}
              label={intl.formatMessage({id:'forms.signUp.enterCode', description:'string'})}
              margin="normal"
              name="code"
              onBlur={formikCode.handleBlur}
              onChange={formikCode.handleChange}
              type="text"
              value={formikCode.values.code}
              variant="filled"
              inputProps={{
                title: intl.formatMessage({id:'forms.signUp.enterCode', description:'string'})
              }}
            />
          </Box>
          { resetError.length > 0 && (
            <div className={styles.errorMessage__container}>
              <label className={styles.errorMessage}>{resetError}</label>
            </div>
          )}
          <div className={styles.infoMessageSubmit__container}>
            <Box display="flex" flexDirection="column">
              { codeResendStatus === 'SENDING' ? (
                  <label className={styles.infoMessage}>
                    <FormattedMessage id="forms.signUp.codeSending" />
                  </label>
                ) : (
                  codeResendStatus === 'SENT' ? (
                    <label className={styles.infoMessage}>
                      <FormattedMessage id="forms.signUp.codeResent" />
                    </label>
                  ) : (
                    <label className={styles.infoMessage}>
                      <span className={styles.fakeLink} onClick={() => resendCode()}>
                        <FormattedMessage id="forms.signUp.resendCode" />
                      </span>
                    </label>
                  )
                )
              }
            </Box>
            <label className={styles.infoMessage}>
              <Link to={'/login'}>
                <FormattedMessage id="forms.resetPassword.backToSignIn" />
              </Link>
            </label>
            <Button
              color="secondary"
              disabled={submitting}
              size="large"
              type="submit"
              variant="contained"
            >
              { submitting ? (
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">
                    <FormattedMessage id="forms.signIn.loading" />
                  </span>
                </div>
              ) : (
                <FormattedMessage id="forms.resetPassword.submit" />
              )}
            </Button>
          </div>
        </form>
      );
    }
  }

  return (
    <>
      <div className={styles.loginFlow__form}>
        <h1 className={styles.loginFlowTitle}>
          <FormattedMessage id="forms.signUp.verify" />
        </h1>
      </div>
      <hr className={layoutStyles.separatorBold} />
      {render()}
    </>
  )
}

VerifyUser.propTypes = {
  setAuthState: PropTypes.func.isRequired,
  setUser: PropTypes.func.isRequired,
  setUsername: PropTypes.func.isRequired,
  setPassword: PropTypes.func.isRequired,
  username: PropTypes.string.isRequired,
  password: PropTypes.string.isRequired,
}

export default VerifyUser;